import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { cad } from "@/lib/pricing";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Lock, ArrowRight, Sparkles } from "lucide-react";
import { VisitsUpsellBanner } from "@/components/merchant/VisitsUpsellBanner";

export default function MerchantAnalytics() {
  const [data, setData] = useState(null);
  const [visits, setVisits] = useState(null);
  const [overage, setOverage] = useState(null);
  useEffect(() => {
    api.get("/merchant/analytics").then((r) => setData(r.data));
    api.get("/merchant/website-visits").then((r) => setVisits(r.data)).catch(() => {});
    api.get("/merchant/overage").then((r) => setOverage(r.data)).catch(() => {});
  }, []);
  if (!data) return <div>Loading...</div>;

  const locked = !!data.advanced_locked;
  const unlockPlan = data.pricing_plan ? "Starter & Pro" : "Growth Plan";
  // On locked plans the backend omits these — fall back to empty placeholders
  // so the blurred UI still renders something meaningful behind the overlay.
  const series = (data.clicks_over_time && data.clicks_over_time.length
    ? data.clicks_over_time
    : locked
      // Fake shape purely for the blurred preview — never surfaced as real data.
      ? [{date:"Mon",clicks:24},{date:"Tue",clicks:41},{date:"Wed",clicks:33},{date:"Thu",clicks:58},{date:"Fri",clicks:47},{date:"Sat",clicks:72},{date:"Sun",clicks:63}]
      : [{ date: "Today", clicks: 0 }]);
  const topCoupons = data.top_coupons && data.top_coupons.length
    ? data.top_coupons
    : locked
      ? [{title:"Sample Offer A", clicks: 128, views: 812},{title:"Sample Offer B", clicks: 94, views: 705},{title:"Sample Offer C", clicks: 61, views: 402}]
      : [];

  return (
    <div data-testid="merchant-analytics">
      <h2 className="font-display text-3xl font-semibold tracking-tight">Analytics</h2>
      <p className="text-sm text-muted-foreground mt-1">Understand traffic and conversion performance.</p>
      {visits && <VisitsUpsellBanner visits={visits} />}

      {data.headline ? (
        <BasicHeadline data={data} />
      ) : (
      <div className="grid md:grid-cols-4 gap-4 mt-6">
        <Metric label="Clicks" value={data.total_clicks} />
        <Metric label="Views" value={data.total_views} />
        <Metric label="CTR" value={`${data.ctr}%`} />
        <Metric label="Revenue" value={cad((data.revenue || 0))} />
      </div>
      )}

      {/* Website visits — the new referral-driven top-of-funnel metric. */}
      {visits && !data.headline && (
        <>
          <div className="grid md:grid-cols-2 gap-4 mt-4" data-testid="website-visits-row">
            <Metric
              label="Website visits — this month"
              value={(visits.website_visits_this_month || 0).toLocaleString()}
              testid="metric-website-visits-mtd"
              hint="Billable clicks Cyannect has sent to your site since the 1st"
            />
            <Metric
              label="Website visits — all time"
              value={(visits.website_visits_total || 0).toLocaleString()}
              testid="metric-website-visits-total"
              hint="Every billable referral click since your first offer went live"
            />
          </div>
          {visits.visit_pack && (
            <div className="pf-card p-5 mt-4" data-testid="visit-pack-quota">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-widest text-cyan-700 font-semibold">Website Visits Pack</div>
                  <div className="text-sm text-slate-900 mt-1 font-medium">
                    {visits.visit_pack.used_this_month.toLocaleString()} of {visits.visit_pack.size.toLocaleString()} visits used this month
                  </div>
                </div>
                <a href="/merchant/billing" className="text-xs font-semibold text-cyan-700 hover:text-cyan-800">Manage add-on →</a>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-3">
                <div
                  className="h-2 bg-gradient-to-r from-cyan-500 to-emerald-500 transition-all"
                  style={{ width: `${Math.min(100, (visits.visit_pack.used_this_month / Math.max(1, visits.visit_pack.size)) * 100).toFixed(1)}%` }}
                />
              </div>
              <div className="text-[11px] text-slate-500 mt-2">
                {visits.visit_pack.remaining.toLocaleString()} visits left{visits.visit_pack.topup_visits > 0 ? ` (includes ${visits.visit_pack.topup_visits.toLocaleString()} top-up visits this month)` : ""} · resets on the 1st. Overage clicks still redirect real shoppers — they just don't count against your pack.
              </div>
            </div>
          )}
          {overage && overage.overage_clicks_this_month > 0 && (
            <div className="pf-card p-5 mt-4 border border-amber-200 bg-amber-50/40" data-testid="overage-preview">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-widest text-amber-800 font-semibold">Over-quota charges this month</div>
                  <div className="text-sm text-slate-900 mt-1 font-medium">
                    {overage.overage_clicks_this_month.toLocaleString()} extra visits × ${(overage.price_per_click_minor / 100).toFixed(2)} = <span className="font-semibold" data-testid="overage-charge">{overage.charge_display}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">Charged on your next Stripe cycle — upgrade to a larger pack anytime to avoid overage.</div>
                </div>
                <a href="/merchant/billing" className="text-xs font-semibold text-amber-800 hover:text-amber-900">Upgrade pack →</a>
              </div>
            </div>
          )}
        </>
      )}

      <LockableSection locked={locked} label="Clicks — last 7 days" testid="analytics-locked-clicks" unlockPlan={unlockPlan}>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Line type="linear" dataKey="clicks" stroke="#0044FF" strokeWidth={2} dot={{ fill: "#0044FF" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </LockableSection>

      <LockableSection locked={locked} label="Top coupons" flush testid="analytics-locked-top" unlockPlan={unlockPlan}>
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-5 py-3">Coupon</th>
              <th className="px-5 py-3">Clicks</th>
              <th className="px-5 py-3">Views</th>
            </tr>
          </thead>
          <tbody>
            {topCoupons.map((c, i) => (
              <tr key={i} className="border-t border-border">
                <td className="px-5 py-3 font-medium">{c.title}</td>
                <td className="px-5 py-3">{c.clicks}</td>
                <td className="px-5 py-3">{c.views}</td>
              </tr>
            ))}
            {topCoupons.length === 0 && (
              <tr><td colSpan={3} className="px-5 py-8 text-center text-muted-foreground">No data yet.</td></tr>
            )}
          </tbody>
        </table>
      </LockableSection>
    </div>
  );
}

function BasicHeadline({ data }) {
  const h = data.headline;
  return (
    <div className="mt-6" data-testid="analytics-basic-headline">
      <div className="text-xs uppercase tracking-widest text-cyan-700 font-semibold">
        {data.plan_label} plan · last {data.headline_days} days
      </div>
      <div className="grid md:grid-cols-4 gap-4 mt-3">
        <Metric label="Verified sales" value={h.verified_sales} testid="basic-headline-sales" />
        <Metric label="Attributed revenue" value={`CA$${h.attributed_revenue_cad}`} testid="basic-headline-revenue" />
        <Metric label="Cyannect fees" value={`CA$${h.cyannect_fees_cad}`} testid="basic-headline-fees" />
        <Metric label="Clicks" value={h.clicks} testid="basic-headline-clicks" />
      </div>
      <p className="text-xs text-slate-500 mt-3" data-testid="basic-headline-note">
        Charts, exports and insights are part of Starter and Pro.{" "}
        <Link to="/merchant/billing" className="text-cyan-700 font-semibold hover:underline" data-testid="basic-headline-upgrade">Change plan →</Link>
      </p>
    </div>
  );
}

function LockableSection({ locked, label, flush, testid, children, unlockPlan = "Growth Plan" }) {
  return (
    <div className={`pf-card ${flush ? "overflow-hidden" : "p-6"} mt-8 relative`} data-testid={testid}>
      {label && !flush && <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">{label}</div>}
      {label && flush && <div className="px-5 py-4 border-b border-border font-display text-lg font-medium">{label}</div>}
      <div className={locked ? "pointer-events-none select-none filter blur-md opacity-70" : ""}>{children}</div>
      {locked && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-sm rounded-2xl" data-testid={`${testid}-overlay`}>
          <div className="text-center max-w-sm px-6">
            <Lock className="w-6 h-6 text-slate-600 mx-auto" />
            <div className="inline-flex items-center gap-1.5 mt-2 text-cyan-700 text-[10px] uppercase tracking-widest font-semibold">
              <Sparkles className="w-3 h-3" /> {unlockPlan} feature
            </div>
            <div className="font-display text-lg text-slate-900 mt-1">Unlock advanced analytics</div>
            <p className="text-xs text-slate-600 mt-1">
              {unlockPlan === "Growth Plan"
                ? "Top offers, geo, funnel — plus lower commission (3.99%) and unlimited campaigns."
                : "Daily trends and top offers — plus lower performance fees on every verified sale."}
            </p>
            <Link to="/merchant/billing" className="pf-btn-primary mt-4 px-5 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                  data-testid={`${testid}-cta`}>
              {unlockPlan === "Growth Plan" ? "Upgrade to Growth" : "Change plan"} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, testid, hint }) {
  return (
    <div className="pf-card p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-display text-3xl font-semibold mt-2">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}
